import { useState } from 'react';
import { promoCodes, type PromoCode } from '@/mocks/promos';
import { useScrollReveal, useStaggerReveal } from '@/hooks/useScrollReveal';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });

const isExpiringSoon = (iso: string) => {
  const diff = new Date(iso).getTime() - Date.now();
  return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000;
};

const isExpired = (iso: string) => new Date(iso).getTime() < Date.now();

const PromoCodeCard = ({ promo }: { promo: PromoCode }) => {
  const [copied, setCopied] = useState(false);
  const expired = isExpired(promo.expiresAt);
  const expiringSoon = isExpiringSoon(promo.expiresAt);
  const usagePercent = Math.round(((promo.usesTotal - promo.usesLeft) / promo.usesTotal) * 100);

  const handleCopy = () => {
    if (expired) return;
    navigator.clipboard.writeText(promo.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div
      className="relative rounded-2xl overflow-hidden flex flex-col transition-all duration-300"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: `1px solid ${expired ? 'rgba(255,255,255,0.06)' : `${promo.color}25`}`,
        opacity: expired ? 0.5 : 1,
      }}
    >
      {/* Top accent */}
      {!expired && (
        <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg, ${promo.color}, transparent)` }} />
      )}

      {/* Badges row */}
      <div className="absolute top-3 right-3 flex flex-col gap-1 items-end z-10">
        {promo.isNew && !expired && (
          <span className="px-2 py-0.5 rounded-full font-mono text-[9px] font-bold" style={{ background: '#39ff14', color: '#050508' }}>
            НОВЫЙ
          </span>
        )}
        {promo.isHot && !expired && (
          <span className="px-2 py-0.5 rounded-full font-mono text-[9px] font-bold" style={{ background: '#ff2d78', color: '#fff' }}>
            🔥 ХИТ
          </span>
        )}
        {expiringSoon && !expired && (
          <span className="px-2 py-0.5 rounded-full font-mono text-[9px] font-bold animate-pulse" style={{ background: '#ff6b00', color: '#fff' }}>
            СКОРО ИСТЕКАЕТ
          </span>
        )}
        {expired && (
          <span className="px-2 py-0.5 rounded-full font-mono text-[9px]" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)' }}>
            ИСТЁК
          </span>
        )}
      </div>

      <div className="p-5 flex flex-col flex-1">
        {/* Icon + category */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${promo.color}15`, border: `1px solid ${promo.color}25` }}>
            <i className={`${promo.icon} text-sm`} style={{ color: promo.color }} />
          </div>
          <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>{promo.category}</span>
        </div>

        {/* Title + discount */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-orbitron font-bold text-sm text-white leading-snug">{promo.title}</h3>
          <div
            className="shrink-0 px-3 py-1 rounded-xl font-orbitron font-black text-sm whitespace-nowrap"
            style={{
              background: `${promo.color}15`,
              border: `1px solid ${promo.color}40`,
              color: promo.color,
              textShadow: `0 0 8px ${promo.color}60`,
            }}
          >
            {promo.discount}
          </div>
        </div>

        <p className="font-mono text-xs leading-relaxed mb-4 flex-1" style={{ color: 'rgba(255,255,255,0.38)' }}>
          {promo.description}
        </p>

        {/* Conditions */}
        <div className="flex flex-wrap gap-3 mb-4">
          {promo.minOrder > 0 && (
            <div className="flex items-center gap-1.5 font-mono text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              <i className="ri-shopping-bag-line text-xs" style={{ color: promo.color }} />
              от {promo.minOrder.toLocaleString()} ₽
            </div>
          )}
          <div className="flex items-center gap-1.5 font-mono text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
            <i className="ri-calendar-line text-xs" style={{ color: promo.color }} />
            до {formatDate(promo.expiresAt)}
          </div>
        </div>

        {/* Usage bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>Использовано</span>
            <span className="font-mono text-xs" style={{ color: promo.color }}>{promo.usesTotal - promo.usesLeft} / {promo.usesTotal}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${usagePercent}%`,
                background: `linear-gradient(90deg, ${promo.color}, ${promo.color}80)`,
                boxShadow: `0 0 6px ${promo.color}60`,
              }}
            />
          </div>
          {promo.usesLeft <= 20 && !expired && (
            <p className="font-mono text-xs mt-1" style={{ color: '#ff6b00' }}>
              Осталось {promo.usesLeft} использований!
            </p>
          )}
        </div>

        {/* Code block */}
        <div
          className="flex items-center justify-between gap-2 p-3 rounded-xl cursor-pointer group/code transition-all duration-200"
          style={{
            background: expired ? 'rgba(255,255,255,0.03)' : `${promo.color}08`,
            border: `1px dashed ${expired ? 'rgba(255,255,255,0.1)' : `${promo.color}40`}`,
          }}
          onClick={handleCopy}
        >
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-coupon-3-line text-sm shrink-0" style={{ color: expired ? 'rgba(255,255,255,0.2)' : promo.color }} />
            <span
              className="font-orbitron font-black text-base tracking-widest truncate"
              style={{
                color: expired ? 'rgba(255,255,255,0.2)' : promo.color,
                textShadow: expired ? 'none' : `0 0 10px ${promo.color}50`,
                letterSpacing: '0.15em',
              }}
            >
              {promo.code}
            </span>
          </div>
          {!expired && (
            <button
              className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs whitespace-nowrap cursor-pointer transition-all duration-200"
              style={{
                background: copied ? promo.color : `${promo.color}15`,
                color: copied ? '#050508' : promo.color,
                border: `1px solid ${promo.color}40`,
              }}
            >
              <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'} />
              {copied ? 'Скопировано!' : 'Копировать'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const PromoCodesSection = () => {
  const [filter, setFilter] = useState<'all' | 'active' | 'new'>('active');
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal({ threshold: 0.1 });
  const { ref: infoRef, isVisible: infoVisible } = useScrollReveal({ threshold: 0.1 });

  const filtered = promoCodes.filter(p => {
    if (filter === 'active') return !isExpired(p.expiresAt);
    if (filter === 'new') return p.isNew && !isExpired(p.expiresAt);
    return true;
  });

  const { containerRef: cardsRef, visibleItems } = useStaggerReveal(filtered.length, { threshold: 0.04 });

  const activeCount = promoCodes.filter(p => !isExpired(p.expiresAt)).length;

  return (
    <section className="px-6 lg:px-12 pb-20">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)',
          }}
        >
          <div>
            <p className="font-mono text-xs tracking-[0.3em] mb-2" style={{ color: 'rgba(0,245,255,0.6)' }}>ПРОМОКОДЫ</p>
            <h2 className="font-orbitron font-black text-3xl md:text-4xl text-white">
              КОДЫ НА<br />
              <span style={{ color: '#00f5ff', textShadow: '0 0 20px rgba(0,245,255,0.5)' }}>СКИДКИ</span>
            </h2>
            <p className="font-mono text-xs mt-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
              {activeCount} активных промокода · Вводите при оформлении заказа
            </p>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {([
              { key: 'active', label: 'Активные' },
              { key: 'new', label: 'Новые' },
              { key: 'all', label: 'Все' },
            ] as const).map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className="px-4 py-2 rounded-lg font-mono text-xs cursor-pointer transition-all duration-200 whitespace-nowrap"
                style={{
                  background: filter === tab.key ? '#00f5ff' : 'transparent',
                  color: filter === tab.key ? '#050508' : 'rgba(255,255,255,0.4)',
                  boxShadow: filter === tab.key ? '0 0 12px rgba(0,245,255,0.4)' : 'none',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* How to use banner */}
        <div
          ref={infoRef as React.RefObject<HTMLDivElement>}
          className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 rounded-2xl mb-8"
          style={{
            background: 'rgba(0,245,255,0.04)',
            border: '1px solid rgba(0,245,255,0.12)',
            opacity: infoVisible ? 1 : 0,
            transform: infoVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.6s cubic-bezier(0.22,1,0.36,1) 0.1s, transform 0.6s cubic-bezier(0.22,1,0.36,1) 0.1s',
          }}
        >
          <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ background: 'rgba(0,245,255,0.1)', border: '1px solid rgba(0,245,255,0.2)' }}>
            <i className="ri-information-line text-lg" style={{ color: '#00f5ff' }} />
          </div>
          <div className="flex-1">
            <p className="font-mono text-xs text-white mb-1">Как использовать промокод?</p>
            <p className="font-mono text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.35)' }}>
              Скопируйте код → добавьте товары в корзину → введите код в поле «Промокод» при оформлении заказа → скидка применится автоматически.
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {['Скопировать', 'В корзину', 'Ввести код'].map((step, i) => (
              <div key={step} className="flex items-center gap-2">
                <div className="flex flex-col items-center gap-1">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center font-orbitron font-bold text-xs" style={{ background: 'rgba(0,245,255,0.15)', color: '#00f5ff', border: '1px solid rgba(0,245,255,0.3)' }}>
                    {i + 1}
                  </div>
                  <span className="font-mono text-[9px] whitespace-nowrap" style={{ color: 'rgba(255,255,255,0.3)' }}>{step}</span>
                </div>
                {i < 2 && <i className="ri-arrow-right-line text-xs mb-3" style={{ color: 'rgba(0,245,255,0.3)' }} />}
              </div>
            ))}
          </div>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <i className="ri-coupon-line text-4xl mb-3 block" style={{ color: 'rgba(255,255,255,0.1)' }} />
            <p className="font-mono text-sm" style={{ color: 'rgba(255,255,255,0.3)' }}>Промокоды не найдены</p>
          </div>
        ) : (
          <div
            ref={cardsRef as React.RefObject<HTMLDivElement>}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          >
            {filtered.map((promo, i) => (
              <div
                key={promo.id}
                style={{
                  opacity: visibleItems[i] ? 1 : 0,
                  transform: visibleItems[i] ? 'translateY(0)' : 'translateY(24px)',
                  transition: 'opacity 0.55s cubic-bezier(0.22,1,0.36,1), transform 0.55s cubic-bezier(0.22,1,0.36,1)',
                }}
              >
                <PromoCodeCard promo={promo} />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PromoCodesSection;
